import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, MessageSquareText, MessageCircle, History, ArrowLeftRight, Lock, Hand, RotateCcw, Ban } from 'lucide-react';
import { useQueue } from '../../context/QueueContext';
import { StatusBadge, formatDate } from './AdminDashboard';
import { formatBRL, formatMeters, getProduct } from '../../data/catalog';
import { getSellers, getUser, getUserArticle, ADMIN_REASONS } from '../../data/users';
import {
  VARIANT_STATUS,
  VARIANT_STATUS_LABELS,
  getVariantStatus,
  setVariantStatus,
  useAvailabilityTick,
} from '../../utils/availability';

const AVAILABILITY = [
  { key: 'disponivel', label: 'Disponível' },
  { key: 'parcial', label: 'Parcialmente disponível' },
  { key: 'indisponivel', label: 'Indisponível' },
];

const parseNum = (v) => parseFloat(String(v).replace(',', '.')) || 0;
const fmtM = (n) => `${Number(n).toFixed(2).replace('.', ',')} m`;
// Pedidos com metragem opcional (cor/estampa procurada pelo cliente) podem não ter metragem.
const fmtReq = (n) => (n == null ? 'a confirmar' : fmtM(n));

// Quantidade que entra no cálculo: indisponível zera; parcial usa a quantidade disponível informada.
// st.qty é TEXTO de tela (ex.: "1,5"); parseNum converte — vazio/zero mantêm o fallback antigo.
const effectiveQty = (item, st) => (st.availability === 'indisponivel' ? 0 : parseNum(st.qty) || item.meters);

const TONE_NOTICE =
  'Algumas observações importantes:\n' +
  '• As imagens do site são ilustrativas. Se preferir, podemos enviar uma foto real do tecido antes de você decidir — é só pedir por aqui mesmo.\n' +
  '• As cores exibidas nas fotos podem apresentar pequenas variações em relação ao tecido real, dependendo das configurações de tela, monitor ou dispositivo utilizado.\n' +
  '• Nosso estoque é dinâmico, então esta solicitação ainda não reserva a quantidade informada. A disponibilidade fica garantida após a negociação e o pagamento combinados com o atendente.\n' +
  'Ficamos à disposição para qualquer dúvida!';

// Frase de continuidade varia conforme a combinação dos status definidos pelo vendedor
// para cada item (mesma fonte dos botões Disponível/Parcialmente disponível/Indisponível).
const continuationText = (avails) => {
  const allOut = avails.every((a) => a === 'indisponivel');
  const anyOut = avails.some((a) => a === 'indisponivel');
  const anyPartial = avails.some((a) => a === 'parcial');
  if (allOut) {
    return `No momento não temos ${avails.length > 1 ? 'esses tecidos' : 'esse tecido'} disponível. Se desejar, responda esta mensagem e podemos verificar outras opções semelhantes para você.`;
  }
  if (anyOut) {
    return 'Alguns itens estão indisponíveis no momento. Se estiver de acordo com os itens disponíveis, responda esta mensagem para continuarmos seu atendimento — e, se desejar, verificamos opções semelhantes para os itens indisponíveis.';
  }
  if (anyPartial) {
    return 'No momento temos uma quantidade menor do que a solicitada. Se essa metragem atender à sua necessidade, responda esta mensagem para continuarmos seu atendimento.';
  }
  return 'Se estiver de acordo, responda esta mensagem para continuarmos seu atendimento.';
};

function buildMessage(request, itemsState, total, sellerUser = null) {
  const firstName = request.customer.split(' ')[0];
  // Cabeçalho identifica o vendedor responsável pelo atendimento (dados já existentes de
  // usuário; artigo vem do mapa explícito por id — ver data/users.js).
  const sellerFirst = sellerUser?.name ? sellerUser.name.split(' ')[0] : '';
  const intro = sellerFirst
    ? `Olá, ${firstName}! Aqui é ${getUserArticle(sellerUser.id)} ${sellerFirst} da Huddersfield Tecidos.`
    : `Olá, ${firstName}! Aqui é da Huddersfield Tecidos.`;
  let msg = `${intro}\n\nRecebemos sua solicitação #${request.id} e verificamos os itens:\n`;
  request.items.forEach((item, idx) => {
    const st = itemsState[idx] || { qty: item.meters, price: '', availability: 'disponivel', note: '' };
    const price = parseNum(st.price);
    const qty = parseNum(st.qty) || item.meters;
    msg += `\n*${item.name}${item.variantName ? ` — ${item.variantName}` : ''}*\n`;
    if (st.availability === 'indisponivel') {
      msg += `Solicitado: ${fmtReq(item.meters)}\nSituação: Indisponível no momento.\n`;
    } else if (st.availability === 'parcial') {
      msg += `Solicitado: ${fmtReq(item.meters)}\nDisponível: ${fmtM(parseNum(st.qty))}\n`;
      if (price) msg += `Valor por metro: ${formatBRL(price)}\nTotal do item: ${formatBRL(parseNum(st.qty) * price)}\n`;
      msg += `Situação: Parcialmente disponível.\n`;
    } else {
      msg += `Solicitado: ${fmtReq(item.meters)}\nDisponível: ${fmtReq(qty)}\n`;
      if (price) msg += `Valor por metro: ${formatBRL(price)}\nTotal do item: ${formatBRL(qty * price)}\n`;
      msg += `Situação: Disponível.\n`;
    }
    if (st.note) msg += `Obs: ${st.note}\n`;
  });
  if (total > 0) msg += `\n*TOTAL DA PROPOSTA: ${formatBRL(total)}*\n`;
  const avails = request.items.map((item, idx) => (itemsState[idx] || { availability: 'disponivel' }).availability);
  msg += `\n${continuationText(avails)}\n\n${TONE_NOTICE}`;
  return msg;
}

export default function AdminDetail() {
  const { id } = useParams();
  const {
    requests, user, statuses,
    acceptRequest, returnToQueue, cancelRequest, transferRequest, setStatus, recordProposal, markProposalSent,
  } = useQueue();
  const request = useMemo(() => requests.find((r) => String(r.id) === String(id)), [requests, id]);

  const [itemsState, setItemsState] = useState(() =>
    (request?.items || []).map((i) => ({ qty: i.meters != null ? String(i.meters).replace('.', ',') : '', price: '', availability: 'disponivel', note: '' }))
  );
  // A fila carrega de forma assíncrona: ao abrir o detalhe diretamente (F5/link),
  // request ainda não existe no primeiro render. Sincroniza quando a solicitação chegar.
  useEffect(() => {
    if (request && request.items.length > 0 && itemsState.length !== request.items.length) {
      setItemsState(request.items.map((i) => ({ qty: i.meters != null ? String(i.meters).replace('.', ',') : '', price: '', availability: 'disponivel', note: '' })));
    }
  }, [request]);
  const [message, setMessage] = useState('');
  const [transferTo, setTransferTo] = useState('');
  const [returnReason, setReturnReason] = useState(ADMIN_REASONS[0]);
  const [cancelReason, setCancelReason] = useState(ADMIN_REASONS[0]);
  const [conflict, setConflict] = useState(false);
  const [stockConfirm, setStockConfirm] = useState(null);
  useAvailabilityTick();

  if (!request) {
    return (
      <div className="py-16 text-center">
        <p className="font-serif text-2xl text-navy">Solicitação não encontrada</p>
        <Link to="/admin/dashboard" className="mt-3 inline-block text-sm font-semibold text-navy underline">Voltar ao painel</Link>
      </div>
    );
  }

  const isAdmin = user.role === 'admin';
  const isOwner = request.assignedTo === user.id;
  const seller = getUser(request.assignedTo);

  // Privacidade entre vendedores: fila (não assumida) ou pedido de outro vendedor não abre detalhes.
  if (!isAdmin && !isOwner) {
    return (
      <div className="mx-auto max-w-lg py-14 text-center" data-testid="detail-privacy-block">
        <Lock className="mx-auto h-10 w-10 text-navy/40" strokeWidth={1.5} />
        <p className="mt-4 font-serif text-2xl text-navy">
          {request.assignedTo ? 'Atendimento de outro vendedor' : 'Solicitação aguardando atendimento'}
        </p>
        <p className="mt-2 text-sm text-muted-foreground">
          {request.assignedTo
            ? `Esta solicitação pertence a ${seller?.name}. Somente ${seller?.name} e administradores podem ver os detalhes.`
            : 'Para ver os detalhes comerciais, aceite o atendimento primeiro. A distribuição é neutra: ninguém escolhe pedidos por tamanho ou valor.'}
        </p>
        {!request.assignedTo && (
          <button
            type="button"
            onClick={() => {
              const res = acceptRequest(request.id);
              if (!res.ok) setConflict(true);
            }}
            data-testid="accept-from-detail-button"
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-navy px-7 py-3.5 text-sm font-semibold text-white hover:bg-navy-hover"
          >
            <Hand className="h-4 w-4" /> ACEITAR ATENDIMENTO
          </button>
        )}
        {conflict && (
          <p className="mt-3 text-sm font-medium text-amber-700" role="alert" data-testid="accept-conflict-notice">
            Esta solicitação acabou de ser assumida por outro vendedor.
          </p>
        )}
        <div>
          <Link to="/admin/dashboard" className="mt-6 inline-block text-sm font-semibold text-navy underline">Voltar ao painel</Link>
        </div>
      </div>
    );
  }

  const update = (idx, patch) =>
    setItemsState((prev) => prev.map((s, i) => (i === idx ? { ...s, ...patch } : s)));

  const total = request.items.reduce((sum, item, idx) => {
    const st = itemsState[idx] || { qty: item.meters, price: '', availability: 'disponivel', note: '' };
    const price = parseNum(st.price);
    if (st.availability === 'indisponivel' || !price) return sum;
    return sum + effectiveQty(item, st) * price;
  }, 0);

  const waLink = () => `https://wa.me/55${request.whatsapp.replace(/\D/g, '')}?text=${encodeURIComponent(message)}`;

  return (
    <div data-testid="admin-request-detail">
      <Link to="/admin/dashboard" className="inline-flex items-center gap-1 text-sm font-semibold text-navy hover:underline" data-testid="admin-detail-back">
        <ArrowLeft className="h-4 w-4" /> Voltar
      </Link>

      <div className="mt-4 grid gap-6 lg:grid-cols-[1fr_380px]">
        <div>
          <div className="rounded-2xl border border-navy/10 bg-white p-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h2 className="font-serif text-2xl text-navy" data-testid="admin-detail-number">Solicitação #{request.id}</h2>
              <StatusBadge status={request.status} />
            </div>
            <dl className="mt-4 grid gap-3 text-sm sm:grid-cols-2">
              <div><dt className="text-xs text-muted-foreground">Cliente</dt><dd className="font-medium" data-testid="admin-detail-customer">{request.customer}</dd></div>
              <div><dt className="text-xs text-muted-foreground">WhatsApp</dt><dd className="font-medium">{request.whatsapp}</dd></div>
              <div><dt className="text-xs text-muted-foreground">Data/hora</dt><dd className="font-medium">{formatDate(request.date)}</dd></div>
              <div><dt className="text-xs text-muted-foreground">Atendida por</dt><dd className="font-medium" data-testid="admin-detail-seller">{seller ? seller.name : '— (aguardando na fila)'}</dd></div>
              {request.observations && (
                <div className="sm:col-span-2"><dt className="text-xs text-muted-foreground">Observações</dt><dd className="font-medium">{request.observations}</dd></div>
              )}
            </dl>
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <label htmlFor="admin-status-select" className="text-xs font-semibold text-navy">Alterar status:</label>
              <select
                id="admin-status-select"
                data-testid="admin-status-select"
                value={request.status}
                onChange={(e) => setStatus(request.id, e.target.value)}
                className="h-10 rounded-lg border border-input bg-white px-3 text-sm outline-none focus:border-navy"
              >
                {statuses.map((s) => (
                  <option key={s.key} value={s.key}>{s.label}</option>
                ))}
              </select>
            </div>

            {isAdmin && (
              <div className="mt-4 space-y-4 rounded-xl border border-navy/15 bg-navy-light/50 p-4" data-testid="admin-actions-panel">
                <p className="text-xs font-semibold uppercase tracking-wide text-navy">
                  Ações do administrador — todas exigem confirmação e ficam registradas no histórico
                </p>
                <div data-testid="transfer-panel">
                  <p className="flex items-center gap-2 text-xs font-semibold text-navy">
                    <ArrowLeftRight className="h-4 w-4" /> Transferir atendimento
                  </p>
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <select
                      value={transferTo}
                      onChange={(e) => setTransferTo(e.target.value)}
                      data-testid="transfer-select"
                      aria-label="Selecionar vendedor"
                      className="h-10 rounded-lg border border-input bg-white px-3 text-sm outline-none focus:border-navy"
                    >
                      <option value="">Selecionar vendedor…</option>
                      {getSellers().filter((s) => s.id !== request.assignedTo).map((s) => (
                        <option key={s.id} value={s.id}>{s.name}</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => transferTo && transferRequest(request.id, transferTo)}
                      disabled={!transferTo}
                      data-testid="transfer-confirm-button"
                      className="rounded-full bg-navy px-5 py-2.5 text-xs font-semibold text-white hover:bg-navy-hover disabled:opacity-40"
                    >
                      CONFIRMAR TRANSFERÊNCIA
                    </button>
                  </div>
                </div>
                {request.status !== 'aguardando_atendimento' && request.status !== 'cancelada' && (
                  <div className="border-t border-navy/10 pt-3" data-testid="return-queue-panel">
                    <p className="flex items-center gap-2 text-xs font-semibold text-navy">
                      <RotateCcw className="h-4 w-4" /> Devolver solicitação para a fila (excepcional)
                    </p>
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <select
                        value={returnReason}
                        onChange={(e) => setReturnReason(e.target.value)}
                        data-testid="return-reason-select"
                        aria-label="Motivo da devolução"
                        className="h-10 rounded-lg border border-input bg-white px-3 text-sm outline-none focus:border-navy"
                      >
                        {ADMIN_REASONS.map((r) => <option key={r}>{r}</option>)}
                      </select>
                      <button
                        type="button"
                        onClick={() => returnToQueue(request.id, returnReason)}
                        data-testid="return-confirm-button"
                        className="rounded-full bg-navy px-5 py-2.5 text-xs font-semibold text-white hover:bg-navy-hover"
                      >
                        CONFIRMAR DEVOLUÇÃO À FILA
                      </button>
                    </div>
                  </div>
                )}
                {request.status !== 'cancelada' && (
                  <div className="border-t border-navy/10 pt-3" data-testid="cancel-panel">
                    <p className="flex items-center gap-2 text-xs font-semibold text-red-700">
                      <Ban className="h-4 w-4" /> Cancelar solicitação
                    </p>
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <select
                        value={cancelReason}
                        onChange={(e) => setCancelReason(e.target.value)}
                        data-testid="cancel-reason-select"
                        aria-label="Motivo do cancelamento"
                        className="h-10 rounded-lg border border-input bg-white px-3 text-sm outline-none focus:border-navy"
                      >
                        {ADMIN_REASONS.map((r) => <option key={r}>{r}</option>)}
                      </select>
                      <button
                        type="button"
                        onClick={() => cancelRequest(request.id, cancelReason)}
                        data-testid="cancel-confirm-button"
                        className="rounded-full bg-red-600 px-5 py-2.5 text-xs font-semibold text-white hover:bg-red-700"
                      >
                        CONFIRMAR CANCELAMENTO
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="mt-5 space-y-4">
            {request.items.map((item, idx) => {
              const st = itemsState[idx] || { qty: item.meters, price: '', availability: 'disponivel', note: '' };
              const itemProduct = getProduct(item.slug || item.productSlug);
              const price = parseNum(st.price);
              const itemTotal = st.availability === 'indisponivel' ? 0 : effectiveQty(item, st) * price;
              const statusLabel =
                st.availability === 'parcial' ? 'PARCIALMENTE DISPONÍVEL' : st.availability === 'indisponivel' ? 'INDISPONÍVEL' : 'DISPONÍVEL';
              const vStatus = itemProduct && item.variantId ? getVariantStatus(itemProduct, item.variantId) : null;
              return (
                <div key={item.key || `${item.slug}-${item.variantId}-${idx}`} className="rounded-2xl border border-navy/10 bg-white p-5" data-testid={`admin-item-${item.slug}-${item.variantId}`}>
                  <div className="flex gap-4">
                    {item.image && (
                      <img src={item.image} alt={item.variantName ? `${item.name} - ${item.variantName}` : item.name} className="h-20 w-20 rounded-lg object-cover" loading="lazy" />
                    )}
                    <div>
                      <p className="font-medium">{item.name}</p>
                      {item.customRequest && (
                        <span className="mt-1 inline-block rounded-full bg-amber-100 px-2.5 py-0.5 text-[11px] font-semibold text-amber-800" data-testid={`admin-item-custom-${idx}`}>
                          Cor/estampa procurada pelo cliente
                        </span>
                      )}
                      {item.variantName && <p className="text-sm text-muted-foreground">{item.variantName}</p>}
                      <p className="mt-1 text-sm">Solicitado: <strong>{item.meters ? formatMeters(item.meters) : 'a confirmar'}</strong></p>
                      {vStatus && (
                        <p className="mt-1.5 flex flex-wrap items-center gap-2 text-xs">
                          <span className="text-muted-foreground">Status da variante:</span>
                          <span
                            className={`rounded-full px-2.5 py-0.5 font-semibold ${
                              vStatus === VARIANT_STATUS.DISPONIVEL
                                ? 'bg-emerald-100 text-emerald-800'
                                : vStatus === VARIANT_STATUS.TEMPORARIAMENTE_INDISPONIVEL
                                  ? 'bg-amber-100 text-amber-800'
                                  : vStatus === VARIANT_STATUS.REVISAR
                                    ? 'bg-sky-100 text-sky-800'
                                    : 'bg-slate-200 text-slate-700'
                            }`}
                            data-testid={`item-stock-status-${idx}`}
                          >
                            {vStatus === VARIANT_STATUS.TEMPORARIAMENTE_INDISPONIVEL ? 'Sem estoque' : VARIANT_STATUS_LABELS[vStatus]}
                          </span>
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="mt-4 grid gap-3 sm:grid-cols-2">
                    <div>
                      <label htmlFor={`qty-${idx}`} className="text-xs font-semibold text-navy">Quantidade disponível (m)</label>
                      <input
                        id={`qty-${idx}`}
                        data-testid={`admin-item-qty-${idx}`}
                        inputMode="decimal"
                        value={typeof st.qty === 'number' ? String(st.qty).replace('.', ',') : st.qty ?? ''}
                        onChange={(e) => {
                          // Texto cru (como o campo de preço): permite apagar tudo e digitar
                          // decimais com vírgula; a conversão acontece só nos cálculos.
                          const raw = e.target.value.replace(/[^\d,.]/g, '');
                          update(idx, { qty: raw });
                        }}
                        className="mt-1 h-11 w-full rounded-lg border border-input px-3 text-sm outline-none focus:border-navy"
                      />
                    </div>
                    <div>
                      <label htmlFor={`price-${idx}`} className="text-xs font-semibold text-navy">Preço por metro (R$)</label>
                      <input
                        id={`price-${idx}`}
                        data-testid={`admin-item-price-${idx}`}
                        inputMode="decimal"
                        placeholder="Informado manualmente"
                        value={st.price}
                        onChange={(e) => {
                          const raw = e.target.value.replace(/[^\d,.]/g, '');
                          update(idx, { price: raw });
                        }}
                        className="mt-1 h-11 w-full rounded-lg border border-input px-3 text-sm outline-none focus:border-navy"
                      />
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap items-center justify-between gap-x-4 gap-y-2 rounded-xl bg-navy-light/60 px-4 py-3" data-testid={`admin-item-total-row-${idx}`}>
                    <span
                      className={`rounded-full px-2.5 py-0.5 text-[11px] font-bold tracking-wide ${
                        st.availability === 'indisponivel'
                          ? 'bg-red-100 text-red-700'
                          : st.availability === 'parcial'
                            ? 'bg-amber-100 text-amber-800'
                            : 'bg-emerald-100 text-emerald-800'
                      }`}
                      data-testid={`admin-item-status-${idx}`}
                    >
                      {statusLabel}
                    </span>
                    {st.availability === 'parcial' && (
                      <span className="text-xs text-muted-foreground" data-testid={`admin-item-partial-info-${idx}`}>
                        Solicitado: {fmtReq(item.meters)} · Disponível: {fmtM(parseNum(st.qty))}
                      </span>
                    )}
                    <p className="flex items-baseline gap-2">
                      <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Total deste item</span>
                      <span className="font-serif text-xl text-navy" data-testid={`admin-item-total-${idx}`}>{formatBRL(itemTotal)}</span>
                    </p>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2" role="radiogroup" aria-label="Disponibilidade">
                    {AVAILABILITY.map((a) => (
                      <button
                        key={a.key}
                        type="button"
                        onClick={() => update(idx, { availability: a.key })}
                        data-testid={`admin-item-availability-${idx}-${a.key}`}
                        aria-pressed={st.availability === a.key}
                        className={`rounded-full border px-3.5 py-2 text-xs font-semibold transition-colors ${
                          st.availability === a.key
                            ? a.key === 'indisponivel'
                              ? 'border-red-600 bg-red-600 text-white'
                              : 'border-navy bg-navy text-white'
                            : 'border-navy/20 text-foreground/70 hover:border-navy'
                        }`}
                      >
                        {a.label}
                      </button>
                    ))}
                  </div>
                  <input
                    data-testid={`admin-item-note-${idx}`}
                    value={st.note}
                    onChange={(e) => update(idx, { note: e.target.value })}
                    placeholder="Observação para o cliente (opcional)"
                    aria-label={`Observação do item ${item.name}`}
                    className="mt-3 h-11 w-full rounded-lg border border-input px-3 text-sm outline-none focus:border-navy"
                  />
                  {vStatus === VARIANT_STATUS.DISPONIVEL && (
                    <div className="mt-3 flex flex-wrap gap-2 border-t border-dashed border-navy/15 pt-3">
                      <button
                        type="button"
                        onClick={() => setStockConfirm({ idx, action: 'sem-estoque' })}
                        data-testid={`item-mark-sem-estoque-${idx}`}
                        className="min-h-11 rounded-full border border-amber-400/60 px-4 py-2 text-xs font-semibold text-amber-700 transition-colors hover:bg-amber-50"
                      >
                        MARCAR COMO SEM ESTOQUE
                      </button>
                      <button
                        type="button"
                        onClick={() => setStockConfirm({ idx, action: 'revisar' })}
                        data-testid={`item-mark-revisar-${idx}`}
                        className="min-h-11 rounded-full border border-navy/20 px-4 py-2 text-xs font-semibold text-navy transition-colors hover:bg-navy-light"
                      >
                        MARCAR PARA REVISÃO
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="mt-5 rounded-2xl border border-navy/10 bg-white p-6" data-testid="history-timeline">
            <p className="flex items-center gap-2 text-sm font-semibold text-navy">
              <History className="h-4 w-4" /> Histórico do atendimento
            </p>
            <ol className="mt-3 space-y-2.5 border-l-2 border-navy/15 pl-4">
              {request.history.map((h, i) => (
                <li key={i} className="text-sm" data-testid={`history-entry-${i}`}>
                  <span className="font-semibold text-navy">{h.time}</span>
                  <span className="text-muted-foreground"> — {h.text}</span>
                </li>
              ))}
            </ol>
            <p className="mt-3 text-xs text-muted-foreground">No sistema real, estes registros serão gravados automaticamente pelo backend.</p>
          </div>
        </div>

        <aside className="h-fit space-y-4 lg:sticky lg:top-40">
          <div className="rounded-2xl bg-navy p-6 text-white" data-testid="admin-proposal-total-card">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-white/70">Total da proposta</p>
            <p className="mt-2 font-serif text-3xl" data-testid="admin-proposal-total">{formatBRL(total)}</p>
            {request.proposalValue != null && (
              <p className="mt-1 text-xs text-white/70">Proposta registrada: {formatBRL(request.proposalValue)}</p>
            )}
            <p className="mt-1 text-xs text-white/60">Soma dos totais dos itens disponíveis ou parcialmente disponíveis. Itens indisponíveis não entram no total.</p>
          </div>
          <button
            type="button"
            onClick={() => {
              setMessage(buildMessage(request, itemsState, total, seller || user));
              if (total > 0) recordProposal(request.id, total);
              // Mobile: a prévia nasce abaixo do botão — rola a tela até ela.
              setTimeout(() => {
                document.querySelector('[data-testid="admin-message-preview"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }, 100);
            }}
            data-testid="vendor-generate-whatsapp-msg-button"
            className="flex w-full items-center justify-center gap-2 rounded-full bg-navy px-6 py-3.5 text-sm font-semibold text-white hover:bg-navy-hover"
          >
            <MessageSquareText className="h-4 w-4" /> GERAR MENSAGEM
          </button>
          {message && (
            <div className="scroll-mt-28 rounded-2xl border border-navy/10 bg-white p-5" data-testid="admin-message-preview">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Prévia da mensagem</p>
              <pre className="mt-3 max-h-72 overflow-y-auto whitespace-pre-wrap rounded-lg bg-navy-light p-4 font-sans text-xs leading-relaxed text-foreground" data-testid="admin-message-text">
                {message}
              </pre>
              <a
                href={waLink()}
                target="_blank"
                rel="noreferrer"
                onClick={() => markProposalSent(request.id)}
                data-testid="vendor-send-whatsapp-button"
                className="mt-4 flex w-full items-center justify-center gap-2 rounded-full bg-wa px-6 py-3.5 text-sm font-semibold text-white hover:bg-wa-hover"
              >
                <MessageCircle className="h-4 w-4" /> RESPONDER PELO WHATSAPP
              </a>
            </div>
          )}
        </aside>
      </div>

      {stockConfirm && (
        <div
          className="fixed inset-0 z-[85] flex items-end justify-center bg-black/40 p-4 sm:items-center"
          role="dialog"
          aria-modal="true"
          data-testid="stock-confirm-modal"
          onClick={() => setStockConfirm(null)}
        >
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
            {(() => {
              const item = request.items[stockConfirm.idx];
              const isSemEstoque = stockConfirm.action === 'sem-estoque';
              return (
                <>
                  <p className="font-serif text-lg text-navy">
                    {isSemEstoque ? 'Confirmar que esta variante está sem estoque?' : 'Marcar esta variante para revisão?'}
                  </p>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                    <strong>{item.name}{item.variantName ? ` — ${item.variantName}` : ''}</strong>{' '}
                    {isSemEstoque
                      ? 'deixará de ficar disponível para novas solicitações, mas permanecerá nesta solicitação atual para que você possa responder ao cliente.'
                      : 'ficará bloqueada para novas solicitações até a conferência física, sem sair desta solicitação atual.'}
                  </p>
                  <div className="mt-5 grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => setStockConfirm(null)}
                      data-testid="stock-cancel-button"
                      className="rounded-full border border-navy/20 py-3 text-sm font-semibold text-navy hover:bg-navy-light"
                    >
                      CANCELAR
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        const product = getProduct(item.slug || item.productSlug);
                        if (product) {
                          setVariantStatus({
                            product,
                            variantId: item.variantId,
                            newStatus: isSemEstoque ? VARIANT_STATUS.TEMPORARIAMENTE_INDISPONIVEL : VARIANT_STATUS.REVISAR,
                            user,
                            origin: 'ATENDIMENTO DA SOLICITAÇÃO',
                          });
                        }
                        if (isSemEstoque) update(stockConfirm.idx, { availability: 'indisponivel', qty: 0 });
                        setStockConfirm(null);
                      }}
                      data-testid="stock-confirm-button"
                      className={`rounded-full py-3 text-sm font-semibold text-white ${isSemEstoque ? 'bg-amber-600 hover:bg-amber-700' : 'bg-navy hover:bg-navy-hover'}`}
                    >
                      {isSemEstoque ? 'CONFIRMAR SEM ESTOQUE' : 'CONFIRMAR REVISÃO'}
                    </button>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
}
